import { useMachine } from '@xstate/react'
import { useQueryClient } from '@tanstack/react-query'
import { ErrorBoundary } from 'react-error-boundary'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigationClient } from '@/api-proxy/useProxiedClient'
import NavigationMap from './NavigationMap'
import { MapProvider, useMap } from 'react-map-gl/mapbox'
import { Button, useMantineColorScheme } from '@mantine/core'
import { useDirectionsData } from './useDirectionsData'
import useAutoRefresh from '@/hooks/autoRefresh'
import { useNavigationMapUpdate } from './useNavigationMapUpdate'
import { UseQueryResult } from '@tanstack/react-query'

import { useNavigationQueries } from './useNavigationQueries'
import AlertRenderer from './alerts/AlertRenderer'
import { fromPromise, SnapshotFrom } from 'xstate'
import { Client } from '@/api/main/client'
import MapControlBox from './controls/MapControlBox'
import { Duration, isAfter, sub } from 'date-fns'
import UnexpectedEventsButton from './controls/UnexpectedEventsButton'
import { Events, NavigationState, RouteModuleState } from './types'
import { useCurrentTime } from '@/stores/currentTime'
import RefreshDirectionsToNextRouteButton from './controls/RefreshDirectionsToNextRouteButton'
import RefreshDirectionsToGarageButton from './controls/RefreshDirectionsToGarageButton'
import {
  DataSyncResponse,
  DirectionsResponses,
  DisplayableEventsResponse,
  NavigationDataResponse,
} from '@/api/navigationApi/types.gen'
import PatrolProgress from './controls/PatrolProgress'
import SignedInDriverBox from './controls/SignedInDriverBox'
import StartPauseSkipButtons from './controls/StartPauseSkipButtons'
import ZoomBox from './controls/ZoomBox'
import { navigationMachine } from './navigationMachine'
import { useNavigationMachineFeed } from './useNavigationMachineFeed'
import ErrorState from '../Common/ErrorState'
import { useTranslation } from 'react-i18next'
import CameraCheck from './CameraCheck'
import { resolveStartFlowAction, shouldRenderAlerts } from './startFlow'

interface NavigationMapControllerProps {
  directionsQuery: UseQueryResult<{ data?: DirectionsResponses[200] }>
  directionsEnabled: boolean
  client: Client | undefined
  secondsToDropoff: number
  fullscreen: boolean
  state: SnapshotFrom<typeof navigationMachine>
  send: (event: Events) => void
  isGPSAccuracyLow: boolean
  explicitDirectionsToGarage: () => void
  refreshDirections: () => void
  shouldStartDropoff: boolean
  navigationMode: import('./useDirectionsData').NavigationMode
  shiftEndingStartDate: Date | undefined
  navigationData: NavigationDataResponse | undefined
  detectionsQuery: UseQueryResult<DisplayableEventsResponse>
  dropoffDriveDurationSeconds: number | undefined
}

const NavigationMapController = ({
  directionsQuery,
  directionsEnabled,
  state,
  client,
  fullscreen,
  send,
  isGPSAccuracyLow,
  explicitDirectionsToGarage,
  refreshDirections,
  shouldStartDropoff,
  navigationMode,
  shiftEndingStartDate,
  navigationData,
  detectionsQuery,
  dropoffDriveDurationSeconds,
}: NavigationMapControllerProps) => {
  const now = useCurrentTime(1000) // Single source of truth from store

  useAutoRefresh()

  const { navigationMap: mapRef } = useMap()
  const [zoom, setZoom] = useState(17)
  const [cameraCheckVisible, setCameraCheckVisible] = useState(false)
  const { colorScheme } = useMantineColorScheme()

  const zoomIn = useCallback(() => setZoom(zoom => zoom + 0.5), [])
  const zoomOut = useCallback(() => setZoom(zoom => zoom - 0.5), [])

  // Update the map when the navigation data changes
  useNavigationMapUpdate({
    data: navigationData,
    mapRef,
    zoom,
  })

  const isDriving = state.matches({ [NavigationState.Monitoring]: RouteModuleState.Driving })
  const isPausing = state.matches({ [NavigationState.Monitoring]: RouteModuleState.Pausing })
  const isResuming = state.matches({ [NavigationState.Monitoring]: RouteModuleState.Resuming })
  const isFinishing = state.matches({ [NavigationState.Monitoring]: RouteModuleState.Finishing })
  const isFreeride = state.matches({ [NavigationState.Monitoring]: RouteModuleState.Freeride })
  const isSkipping = state.matches({ [NavigationState.Monitoring]: RouteModuleState.Skipping })

  // Disable buttons during transitions
  const isPauseDisabled = isPausing || isResuming
  const isStartDisabled = isPausing || isResuming
  const isSkipDisabled = isPausing || isResuming || isFreeride || isSkipping

  const wasRecentlySyncing = (duration: Duration): boolean =>
    state.context.status !== null &&
    state.context.status !== 'Waiting' &&
    state.context.updatedAt !== null &&
    isAfter(state.context.updatedAt, sub(now, duration))

  const handleResume = useCallback(() => send({ type: 'REQUEST_RESUME' }), [send])

  const handleStart = useCallback(() => {
    const startAction = resolveStartFlowAction(state.context.navData?.route_module_state)
    if (startAction === 'resume') send({ type: 'REQUEST_RESUME' })
    if (startAction === 'cameraCheck') setCameraCheckVisible(true)
  }, [send, state.context.navData?.route_module_state])

  const handleCameraCheckSuccess = useCallback(() => {
    setCameraCheckVisible(false)
    send({ type: 'REQUEST_START' })
  }, [send])

  const handleCameraCheckCancel = useCallback(() => {
    setCameraCheckVisible(false)
  }, [])
  const handleFinish = useCallback(() => send({ type: 'REQUEST_FINISH' }), [send])
  const handleSkip = useCallback(() => send({ type: 'REQUEST_SKIP' }), [send])
  const handlePause = useCallback(() => send({ type: 'REQUEST_PAUSE' }), [send])

  const showUnexpectedEvents = isDriving || isFreeride || shouldStartDropoff
  const directionButton = shouldStartDropoff ? (
    <RefreshDirectionsToGarageButton onRefresh={explicitDirectionsToGarage} />
  ) : !isDriving && !isFreeride ? (
    <RefreshDirectionsToNextRouteButton onRefresh={refreshDirections} />
  ) : null

  return (
    <NavigationMap
      id="navigationMap"
      cursor={cameraCheckVisible ? 'hidden' : 'regular'}
      navigationData={state.context.navData ?? undefined}
      colorScheme={colorScheme}
      directionsData={directionsQuery?.data?.data}
      directionsEnabled={directionsEnabled}
      initialZoom={zoom}
      height={fullscreen ? '100vh' : undefined}
      detectionsQuery={detectionsQuery}
    >
      <MapControlBox position="left">
        <ZoomBox onZoomIn={zoomIn} onZoomOut={zoomOut} hideRefresh={isFreeride} />
      </MapControlBox>

      <MapControlBox position="right">
        <StartPauseSkipButtons
          onPause={handlePause}
          onStart={handleStart}
          onSkipSegment={handleSkip}
          onResume={handleResume}
          isStartDisabled={isStartDisabled || cameraCheckVisible}
          isPauseDisabled={isPauseDisabled}
          isSkipDisabled={isSkipDisabled}
          moduleState={state.context.navData?.route_module_state}
          hideSkip={isFreeride}
        />
      </MapControlBox>

      {!wasRecentlySyncing({ seconds: 15 }) && (
        <SignedInDriverBox driver={state.context.navData?.driver} client={client} />
      )}

      <UnexpectedEventsButton visible={showUnexpectedEvents} client={client} />
      {directionButton}

      <CameraCheck
        client={client}
        visible={cameraCheckVisible}
        onSuccess={handleCameraCheckSuccess}
        onCancel={handleCameraCheckCancel}
      />

      {shouldRenderAlerts(cameraCheckVisible) && (
        <AlertRenderer
          state={state}
          client={client}
          navigationData={state.context.navData ?? undefined}
          onStart={handleStart}
          onFinish={handleFinish}
          isFinishing={isFinishing}
          navigationMode={navigationMode}
          shiftEndingStartDate={shiftEndingStartDate}
          isGPSAccuracyLow={isGPSAccuracyLow}
          dropoffDriveDurationSeconds={dropoffDriveDurationSeconds}
        />
      )}

      <PatrolProgress navigationData={state.context.navData ?? undefined} />
    </NavigationMap>
  )
}

const ErrorFallback = ({ resetErrorBoundary }: { resetErrorBoundary: () => void }) => {
  const { t } = useTranslation('others')

  return (
    <ErrorState
      title={t('somethingWentWrong')}
      message={t('somethingWentWrongBody')}
      showBackButton={false}
      retryAction={
        <Button onClick={resetErrorBoundary} color="blue">
          Retry
        </Button>
      }
    />
  )
}

const CarNavigationProvider = ({ vehicleId, fullscreen }: { vehicleId: string | undefined; fullscreen: boolean }) => {
  const client = useNavigationClient(vehicleId)
  const queryClient = useQueryClient()

  const { syncQuery, navigationQuery, mutations, isGPSAccuracyLow, detectionsQuery } = useNavigationQueries({
    client,
    vehicleId,
  })

  return (
    <MapProvider>
      <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => queryClient.invalidateQueries()}>
        <NavigationInner
          key={vehicleId}
          vehicleId={vehicleId}
          client={client}
          syncQuery={syncQuery}
          navigationQuery={navigationQuery}
          mutations={mutations}
          isGPSAccuracyLow={isGPSAccuracyLow}
          fullscreen={fullscreen}
          navigationData={navigationQuery.data}
          detectionsQuery={detectionsQuery}
        />
      </ErrorBoundary>
    </MapProvider>
  )
}

const NavigationInner = ({
  vehicleId,
  client,
  syncQuery,
  navigationQuery,
  mutations,
  isGPSAccuracyLow,
  fullscreen,
  navigationData,
  detectionsQuery,
}: {
  vehicleId: string | undefined
  client: Client | undefined
  syncQuery: UseQueryResult<DataSyncResponse>
  navigationQuery: UseQueryResult<NavigationDataResponse>
  // xstate actor logic requires the function-shape mutations directly; the typed return from
  // useNavigationQueries narrows TInput in a way that conflicts with PromiseActorLogic
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutations: any
  isGPSAccuracyLow: boolean
  fullscreen: boolean
  navigationData: NavigationDataResponse | undefined
  detectionsQuery: UseQueryResult<DisplayableEventsResponse>
}) => {
  // Use a Ref Bridge for mutations to ensure they never capture stale scope
  const mutationsRef = useRef(mutations)
  useEffect(() => {
    mutationsRef.current = mutations
  })

  // The machine is created fresh on mount
  const machine = useMemo(
    () =>
      navigationMachine.provide({
        actors: {
          skipMutation: fromPromise(() => mutationsRef.current.skip({})),
          pauseMutation: fromPromise(() => mutationsRef.current.pause({})),
          resumeMutation: fromPromise(() => mutationsRef.current.resume({})),
          finishMutation: fromPromise(() => mutationsRef.current.finish({})),
          startMutation: fromPromise(() => mutationsRef.current.start({})),
        },
      }),
    []
  )

  const [state, send] = useMachine(machine, { input: { vehicleId } })
  const directions = useDirectionsData({ navigationQuery, client })

  useNavigationMachineFeed({
    send,
    syncQuery,
    navigationQuery,
    vehicleId,
    ...directions,
  })

  return (
    <NavigationMapController
      state={state}
      send={send}
      client={client}
      fullscreen={fullscreen}
      isGPSAccuracyLow={isGPSAccuracyLow}
      navigationData={navigationData}
      detectionsQuery={detectionsQuery}
      {...directions}
    />
  )
}
export default CarNavigationProvider
